import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * Due to the way ReactVirtualized calculates positions and the fact that we can have rows with significant height differences,
 * We must rely on the cached cell heights/widths (as opposed to the previous method of aggressively clearing the cache)
 *
 * This class keeps track of which cells were recently computed (i.e., are fresh).  For cells that were not recently computed,
 * we will recompute them the next time they are requested to be measured.  By not aggressively clearing the cache, we can continue to
 * use the previously-measured sizes, which will be more accurate than the default sizes
 *
 * Note that except for the isFreshInBodyGrid(), all usages of rowIndex and columnIndex refer to the convention used in the react-virtualized CellMeasurerCache
 * where in a MultiGrid, the row header column is index 0 and the body grid first column would be 1, and the first body grid row index must be offset by the number of column header rows (DataGrid.fixedRowCount).
 */
export class DataGridCellMeasurementFreshness {
  constructor() {
    // Set by the owing DataGrid to indicate the number of rows in the row header
    _defineProperty(this, "rowIndexOffset", 0);
    // Set by the DataGrid to indicate the number of header column rows (note that with grouped columns, this may be greater than 1)
    _defineProperty(this, "columnIndexOffset", 0);
    _defineProperty(this, "freshRows", []);
  }
  /**
   * Get the number of rows in the cache.
   */
  get rowCount() {
    return this.freshRows.length;
  }

  /**
   * Completely empty the set of cells that are considered fresh
   */
  clearAll() {
    this.freshRows = [];
  }

  /**
   * Remove a single cell from the set of cells that are considered fresh
   * (essentially marking the cell measurement as stale)
   * @param rowIndex Zero-based rowIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your rowIndex to account for the presence of column headers.
   * @param columnIndex Zero-based columnIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your columnIndex to account for the presence of a row header.
   */
  clear(rowIndex, columnIndex) {
    let rowValue = this.freshRows[rowIndex];
    if (rowValue === undefined) {
      return;
    }
    rowValue[columnIndex] = undefined;
  }

  /**
   * Clears cache starting from the indicated startIndex to the end.
   * This is useful when inserting or removing rows, or in a hierarchical grid when expanding/collapsing a node which should invalidate all slots at and below the node.
   * @param startIndex Zero-based rowIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your rowIndex to account for the presence of column headers.
   */
  clearRowToEnd(startIndex) {
    this.freshRows.splice(startIndex, this.freshRows.length - startIndex);
  }

  /**
   * Indicate that a single cell has been measured recently
   * @param rowIndex Zero-based rowIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your rowIndex to account for the presence of column headers.
   * @param columnIndex Zero-based columnIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your columnIndex to account for the presence of a row header.
   */
  markFresh(rowIndex, columnIndex) {
    let rowValue = this.freshRows[rowIndex];
    if (rowValue === undefined) {
      rowValue = [];
      this.freshRows[rowIndex] = rowValue;
    }
    rowValue[columnIndex] = true;
  }

  /**
   * Determine if a single cell has been measured recently
   * @param rowIndex Zero-based rowIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your rowIndex to account for the presence of column headers.
   * @param columnIndex Zero-based columnIndex as used in the CellMeasurerCache. For a body grid cell you have to adjust your columnIndex to account for the presence of a row header.
   */
  isFresh(rowIndex, columnIndex) {
    let rowValue = this.freshRows[rowIndex];
    if (rowValue === undefined) {
      return false;
    }
    return rowValue[columnIndex] === true;
  }

  /**
   * Determine if a single cell has been measured recently
   * @param bodyGridRowIndex Zero-based rowIndex in the bodyGrid
   * @param bodyGridColumnIndex Zero-based columnIndex in the bodyGrid.
   */
  isFreshInBodyGrid(bodyGridRowIndex, bodyGridColumnIndex) {
    let rowIndex = this.rowIndexOffset + bodyGridRowIndex;
    let columnIndex = this.columnIndexOffset + bodyGridColumnIndex;
    return this.isFresh(rowIndex, columnIndex);
  }
}
//# sourceMappingURL=DataGridCellMeasurementFreshness.js.map
